import React, { useEffect, useMemo, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { Formik, ErrorMessage } from 'formik';

import LoadMask from 'components/LoadMask';
import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicButton from 'components/BasicButton';
import BasicTextArea from 'components/BasicTextArea';
import BasicSubmitButton from 'components/BasicSubmitButton';
import BasicInput from 'components/BasicInput';
import BasicLabel from 'components/BasicLabel';
import HorizontalLoader from 'components/HorizontalLoader';
import DatePicker from 'components/DatePicker';
import CreatableLazySelect from 'components/LazyLoadSelect/lazy-load-creatable';

import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';
import { ENDPOINT } from 'constants/endpoints';
import { useApi } from 'hooks/useApi';
import { initialData } from 'scenes/Form/components/FirmPortfolioCreateUpdateItemForm/initialData';
import { Normalizer } from 'scenes/Form/components/FirmPortfolioCreateUpdateItemForm/normalizer';
import { FirmPortfolioCreateUpdateItemFormValidationSchema } from 'scenes/Form/components/FirmPortfolioCreateUpdateItemForm/validate';

import './styles.css';

const FirmPortfolioCreateUpdateItemForm = props => {
  const {
    params: { id, emId, type },
    actions: { proceed, dismiss, handleError },
  } = props;

  const api = useApi();
  const isEditMode = useMemo(() => !!emId, []);
  const [formData, setFormData] = useState(initialData);

  useEffect(() => {
    api.get(
      ENDPOINT.FORM.GET.FIRM_PROFILE_ITEM,
      { id, emId },
      {
        handleError: response => handleError(response),
        handleSuccess: response => setFormData(Normalizer.fetch(response)),
      }
    );
  }, []);

  const getFirmOptions = (query, offset) => {
    return isEditMode
      ? []
      : new Promise((resolve, reject) => {
          api.get(
            ENDPOINT.OTHER.FIRM_PROFILE_ITEM_FIRM_OPTIONS,
            { query, offset, id },
            {
              handleError: response => reject(response),
              handleSuccess: data => resolve(data),
            }
          );
        });
  };

  const onSubmit = (values, { setSubmitting, setErrors }) => {
    api.post(
      ENDPOINT.FORM.SAVE.FIRM_PROFILE_ITEM,
      { ...Normalizer.send({ ...values, emId, Type: type }, isEditMode), id },
      {
        handleError: response => {
          setSubmitting(false);
          handleError(response, setErrors);
        },
        handleSuccess: response => proceed(response),
      }
    );
  };

  return formData.init ? (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <Formik
          initialValues={formData.fields}
          validationSchema={FirmPortfolioCreateUpdateItemFormValidationSchema}
          onSubmit={onSubmit}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => (
            <form onSubmit={handleSubmit} className="form__content">
              {isSubmitting && <LoadMask />}
              <div className="form__group form__group--compact">
                <CreatableLazySelect
                  isDisabled={isEditMode}
                  name="AssignedFirmID"
                  isMulti={false}
                  value={values.AssignedFirmID}
                  isValid={
                    (!errors.AssignedFirmID && !!touched.AssignedFirmID) ||
                    !touched.AssignedFirmID
                  }
                  label="Firm"
                  labelStyles={{
                    fontWeight: 'bold',
                  }}
                  required={true}
                  placeholder="Start Typing"
                  getOption={getFirmOptions}
                  onChange={setFieldValue}
                  onBlur={setFieldTouched}
                />

                <ErrorMessage
                  name="AssignedFirmID"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__group form__group--compact">
                <BasicInput
                  label="Transaction"
                  required={false}
                  name="Transaction"
                  inputType="text"
                  content={values.Transaction}
                  placeholder="Transaction"
                  controlFunc={handleChange}
                  isValid={
                    (!errors.Transaction && touched.Transaction) ||
                    !touched.Transaction
                  }
                  touchFunc={handleBlur}
                  labelStyles={{ fontWeight: 'bold' }}
                />

                <ErrorMessage
                  name="Transaction"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__group form__group--compact">
                <BasicLabel
                  name="Description"
                  required={false}
                  text="Description"
                  styles={{
                    fontWeight: 'bold',
                  }}
                />
                <BasicTextArea
                  rows={10}
                  name="Description"
                  content={values.Description}
                  resize={false}
                  placeholder="0 characters of the 1000 character limit"
                  controlFunc={handleChange}
                  touchFunc={handleBlur}
                  isValid={
                    (!errors.Description && !!touched.Description) ||
                    !touched.Description
                  }
                />

                <ErrorMessage
                  name="Description"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__group form__group--compact">
                <DatePicker
                  title="Date"
                  titleStyles={{
                    display: 'inline-block',
                    marginBottom: '15px',
                    textTransform: 'uppercase',
                    fontFamily: 'Noto Sans, sans-serif',
                    fontSize: '0.9286rem',
                    fontWeight: '600',
                  }}
                  name="Date"
                  styles={{ width: '400px', display: 'block' }}
                  selected={values.Date || ''}
                  onChange={setFieldValue}
                />

                <ErrorMessage
                  name="Date"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__controls form__controls--compact">
                <BasicButtonGroup groupStyles={{ width: '100%' }}>
                  <BasicSubmitButton
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Save"
                  />

                  <BasicButton
                    controlFunc={dismiss}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnArrow={false}
                    btnTextColor="#27282d"
                    btnText="Cancel"
                  />
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  ) : (
    <div className="form__inner">
      <div className="horizontal-loader__container">
        <div className="horizontal-loader__container">
          <HorizontalLoader />
        </div>
      </div>
    </div>
  );
};

export default FirmPortfolioCreateUpdateItemForm;
